import { useMemo } from 'react';
import { parseCsvForPreview } from './csvPreviewParse';

/**
 * エクスポートのプレビュー表示 (オーナー要望 2026-09-18)。
 *
 * これまで投稿・参加者のエクスポートは押すと即ダウンロードで、列構成や値の解決結果を
 * 開くまで確認できなかった。callable が返す **実際の出力そのもの** (先頭数件に切詰めたもの) を
 * ここに流し込むので、表示内容とダウンロードされる中身が必ず一致する。
 *
 * - CSV: 表として描画 (列が多いので横スクロール)。
 * - JSON / GeoJSON: 整形済テキストをそのまま表示。
 */

export interface ExportPreviewPanelProps {
  /** 表示する内容 (callable の content、プレビューなら先頭数件に切詰め済)。 */
  content: string;
  /** csv は表、それ以外はテキストで描画する。 */
  format: 'csv' | 'json' | 'geojson';
  /** 出力対象の総件数 (プレビューに出ているのは先頭のみ)。 */
  totalRowCount: number;
  /** 見出し (例: 「投稿 CSV のプレビュー」)。 */
  title: string;
  /** 「この内容をダウンロード」ボタン。省略時は非表示。 */
  onDownload?: () => void;
  downloadLabel?: string;
  onClose?: () => void;
}

export function ExportPreviewPanel({
  content,
  format,
  totalRowCount,
  title,
  onDownload,
  downloadLabel = '⬇ この内容で全件をダウンロード',
  onClose,
}: ExportPreviewPanelProps) {
  const table = useMemo(() => {
    if (format !== 'csv') return null;
    return parseCsvForPreview(content);
  }, [content, format]);

  const shownRows = table ? table.rows.length : null;

  return (
    <div className="mt-3 border-t border-slate-200 pt-3">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <h3 className="text-sm font-bold text-slate-900">{title}</h3>
        <span className="text-xs text-slate-500">
          {shownRows !== null
            ? `先頭 ${shownRows} 件を表示 / 全 ${totalRowCount} 件`
            : `全 ${totalRowCount} 件のうち先頭のみ表示`}
        </span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-xs text-primary-600 hover:underline"
          >
            プレビューを閉じる
          </button>
        )}
      </div>

      {table ? (
        table.headers.length === 0 ? (
          <p className="text-xs text-slate-500">出力するデータがありません。</p>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded max-h-96 overflow-y-auto">
            <table className="text-xs whitespace-nowrap">
              <thead className="bg-slate-100 sticky top-0">
                <tr>
                  {table.headers.map((h, i) => (
                    <th
                      key={`${h}-${i}`}
                      className="px-2 py-1 text-left font-semibold text-slate-700 border-b border-slate-200"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row, ri) => (
                  <tr key={ri} className="odd:bg-white even:bg-slate-50">
                    {table.headers.map((h, ci) => (
                      <td
                        key={`${h}-${ci}`}
                        className="px-2 py-1 text-slate-700 border-b border-slate-100 max-w-[18rem] truncate"
                        title={row[ci] ?? ''}
                      >
                        {row[ci] ?? ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <pre className="text-[11px] leading-relaxed bg-slate-50 border border-slate-200 rounded p-2 max-h-96 overflow-auto">
          {content}
        </pre>
      )}

      {onDownload && (
        <button
          type="button"
          onClick={onDownload}
          className="mt-2 text-xs text-primary-600 hover:text-primary-700 hover:underline"
        >
          {downloadLabel}
        </button>
      )}
    </div>
  );
}
